import { cn } from "@/shared/lib/cn";

export type Platform = "telegram" | "vk" | "max" | "ok" | "dzen" | "youtube" | "tiktok";

const letter: Record<string, string> = {
  telegram: "TG", vk: "VK", max: "M", ok: "OK", dzen: "Д", youtube: "YT", tiktok: "TT",
};

function TelegramSvg({ s }: { s: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="12" fill="#2AABEE"/>
      <path fill="white" d="M5.455 11.678 17.82 7.03c.578-.2.982.136.813.965L16.5 18.06c-.147.648-.537.805-1.087.5l-3.02-2.224-1.457 1.4c-.16.16-.3.294-.617.294l.22-3.117 5.68-5.13c.245-.218-.054-.34-.378-.122L6.9 14.1l-2.94-.925c-.638-.198-.65-.638.495-1.497z"/>
    </svg>
  );
}

function VkSvg({ s }: { s: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="6" fill="#0077FF"/>
      <path fill="white" d="M13.162 16.12h-1.474c-4.317 0-6.782-2.968-6.888-7.9h2.298c.074 3.621 1.672 5.155 2.94 5.474V8.22h2.178V11c1.236-.133 2.546-1.545 2.985-2.78h2.165c-.327 1.771-1.598 3.093-2.514 3.631 1 .434 2.47 1.574 3.033 4.269h-2.382c-.45-1.417-1.569-2.524-3.341-2.63v2.63z"/>
    </svg>
  );
}

function MaxSvg({ s }: { s: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="6" fill="#7c3aed"/>
      <path fill="white" d="M5 17V7l4.5 5.5L14 7v10h-2v-5.5L9.5 14.5 7 11.5V17H5zm12-1.5-2-2.5 2-2.5h2.5l-2 2.5 2 2.5H17z"/>
    </svg>
  );
}

function OkSvg({ s }: { s: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="12" fill="#EE8208"/>
      <path fill="white" d="M12 5.5a3 3 0 1 1 0 6 3 3 0 0 1 0-6zm0 1.8a1.2 1.2 0 1 0 0 2.4 1.2 1.2 0 0 0 0-2.4zM8.2 14.4C8.8 13 10.3 12 12 12s3.2 1 3.8 2.4l-1.5.6c-.4-.8-1.2-1.3-2.3-1.3s-1.9.5-2.3 1.3l-1.5-.6zm4 1.1-.9 3h1.8l-.9-3z"/>
    </svg>
  );
}

function DzenSvg({ s }: { s: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="12" fill="#000"/>
      <path fill="white" d="M12 3c.17 3.78-1.05 5-4.87 5H3v2h4.13c3.82 0 5.04 1.22 4.87 5v3h2v-3c-.17-3.78 1.05-5 4.87-5H21v-2h-4.13c-3.82 0-5.04-1.22-4.87-5V3h-2z"/>
    </svg>
  );
}

function YoutubeSvg({ s }: { s: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="5" fill="#FF0000"/>
      <path fill="white" d="M20.6 8a2.5 2.5 0 0 0-1.76-1.76C17.25 5.75 12 5.75 12 5.75s-5.25 0-6.84.49A2.5 2.5 0 0 0 3.4 8C2.9 9.59 2.9 12 2.9 12s0 2.41.5 4a2.5 2.5 0 0 0 1.76 1.76c1.59.49 6.84.49 6.84.49s5.25 0 6.84-.49A2.5 2.5 0 0 0 20.6 16c.5-1.59.5-4 .5-4s0-2.41-.5-4zm-10.1 6.45v-4.9L15 12l-4.5 2.45z"/>
    </svg>
  );
}

function TikTokSvg({ s }: { s: number }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none">
      <rect width="24" height="24" rx="6" fill="#000"/>
      <path fill="white" d="M18 8.1a5.3 5.3 0 0 1-3.1-1V13a4.4 4.4 0 1 1-4.4-4.4c.15 0 .3.01.44.03v2.42a2 2 0 1 0 1.4 1.9V4h2.46a3.1 3.1 0 0 0 3.2 2.8V8.1z"/>
    </svg>
  );
}

const icons: Record<string, (s: number) => React.ReactElement> = {
  telegram: (s) => <TelegramSvg s={s} />,
  vk:       (s) => <VkSvg s={s} />,
  max:      (s) => <MaxSvg s={s} />,
  ok:       (s) => <OkSvg s={s} />,
  dzen:     (s) => <DzenSvg s={s} />,
  youtube:  (s) => <YoutubeSvg s={s} />,
  tiktok:   (s) => <TikTokSvg s={s} />,
};

export function PlatformIcon({ platform, size = 18, mono = false, className }: { platform: Platform | string; size?: number; mono?: boolean; className?: string }) {
  if (mono) {
    return (
      <span
        aria-label={platform}
        className={cn("inline-flex items-center justify-center rounded-full font-display font-bold select-none", className)}
        style={{
          width: size, height: size,
          color: "currentColor",
          fontSize: Math.round(size * 0.45),
          border: "1.5px solid currentColor",
          opacity: 0.6,
        }}
      >
        {letter[platform] ?? platform.slice(0, 2).toUpperCase()}
      </span>
    );
  }

  const render = icons[platform];
  if (!render) {
    return (
      <span
        aria-label={platform}
        className={cn("inline-flex items-center justify-center rounded-full bg-ink-20 text-white font-display font-bold select-none", className)}
        style={{ width: size, height: size, fontSize: Math.round(size * 0.45) }}
      >
        {platform.slice(0, 2).toUpperCase()}
      </span>
    );
  }

  return (
    <span aria-label={platform} className={cn("inline-flex shrink-0 select-none", className)}>
      {render(size)}
    </span>
  );
}
